/**
 * Bulk Import API — single CSV for clients + projects, and timesheet import
 */
import { API_BASE_URL } from '../utils/constants';
import { storage } from '../utils/helpers';

// ── Types ───────────────────────────────────────────────────

export interface ClientPreview {
  name: string;
  project_count: number;
  is_new: boolean;
}

export interface ProjectPreview {
  row: number;
  project_name: string;
  project_code: string;
  project_type: string | null;
  project_type_raw: string;
  client_name: string;
  is_new_client: boolean;
  manager_name: string;
  manager_found: boolean;
  sales_code: string;
  sales_name: string | null;
  sales_found: boolean;
  status: string;
  is_billable: boolean;
  start_date: string | null;
  end_date: string | null;
  warnings: string[];
}

export interface PreviewResponse {
  total_rows: number;
  clients: ClientPreview[];
  clients_new: number;
  clients_existing: number;
  projects: ProjectPreview[];
  managers_not_found: string[];
  sales_not_found: string[];
  warnings_count: number;
  warnings: string[];
}

export interface ExecuteResponse {
  message: string;
  clients_created: number;
  projects_created: number;
  warnings_count: number;
  warnings: string[];
}

// ── Timesheet Import Types ──────────────────────────────────

export interface TimesheetPreviewRow {
  row: number;
  date: string;
  employee_email: string;
  employee_name: string;
  employee_code: string;
  employee_found: boolean;
  employee_id: number | null;
  project: string;
  project_found: boolean;
  project_id: number | null;
  task: string;
  task_new: boolean;
  hours: number | null;
  description: string;
  status: string;
  reject_reason: string;
  approved_by: string;
  is_duplicate: boolean;
  valid: boolean;
  warnings: string[];
}

export interface TimesheetPreviewResponse {
  total_rows: number;
  rows_previewed: number;
  valid_rows: number;
  invalid_rows: number;
  employees_found: number;
  employees_missing: string[];
  employees_missing_count: number;
  projects_found: number;
  projects_missing: string[];
  projects_missing_count: number;
  task_types_existing: number;
  task_types_new: string[];
  task_types_new_count: number;
  duplicates_to_update: number;
  new_to_create: number;
  preview_rows: TimesheetPreviewRow[];
  warnings_count: number;
  warnings: string[];
}

// Quick preview for large files (fast, minimal validation)
export interface QuickPreviewResponse {
  total_rows: number;
  columns: string[];
  sample_rows: Array<{
    row: number;
    date: string;
    employee: string;
    project: string;
    task: string;
    hours: string;
  }>;
  message: string;
  use_chunked_import: boolean;
}

export interface TimesheetExecuteResponse {
  message: string;
  created: number;
  updated: number;
  skipped: number;
  task_types_created: string[];
  task_types_created_count: number;
  projects_updated: number;
  warnings_count: number;
  warnings: string[];
  // Debug fields for troubleshooting
  debug_log?: string[];
  execution_time_seconds?: number;
  // Error fields (when status is 500)
  error?: string;
  error_type?: string;
  traceback?: string;
  partial_results?: {
    created: number;
    updated: number;
    skipped: number;
  };
}

// ── Helpers ─────────────────────────────────────────────────

async function uploadCSV<T>(endpoint: string, file: File): Promise<T> {
  const token = storage.getToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }
  return data as T;
}

async function uploadCSVWithOptions<T>(
  endpoint: string,
  file: File,
  options: Record<string, string>
): Promise<T> {
  const token = storage.getToken();
  const formData = new FormData();
  formData.append('file', file);
  
  // Append extra form fields
  Object.entries(options).forEach(([key, value]) => {
    formData.append(key, value);
  });

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  // Try to parse JSON response
  let data: any;
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    // Non-JSON response (likely HTML error page)
    const text = await response.text();
    throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 200)}`);
  }
  
  if (!response.ok) {
    // Return the full error data including debug_log for troubleshooting
    const error = new Error(data.message || `HTTP Error: ${response.status}`) as any;
    error.response = data;  // Attach full response for debugging
    throw error;
  }
  return data as T;
}

async function downloadTemplate(): Promise<void> {
  const token = storage.getToken();
  const response = await fetch(`${API_BASE_URL}/import/template`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'import_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function downloadTimesheetTemplate(): Promise<void> {
  const token = storage.getToken();
  const response = await fetch(`${API_BASE_URL}/import/timesheet-template`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'timesheet_import_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ── API ─────────────────────────────────────────────────────

export interface TimesheetExecuteOptions {
  updateProjects?: boolean;
}

// ── Chunked Import Types ────────────────────────────────────

export interface TimesheetUploadResponse {
  job_id: string;
  total_rows: number;
  message: string;
}

export interface ChunkExecuteResponse {
  status: 'in_progress' | 'completed' | 'error';
  chunk_processed: number;
  chunk_created: number;
  chunk_updated: number;
  chunk_skipped: number;
  total_rows: number;
  processed_rows: number;
  created: number;
  updated: number;
  skipped: number;
  next_offset: number | null;
  progress_percent: number;
  warnings_count: number;
  warnings: string[];
  // Error fields
  message?: string;
  error?: string;
  traceback?: string;
}

export interface JobStatusResponse {
  job_id: string;
  status: string;
  total_rows: number;
  processed_rows: number;
  created: number;
  updated: number;
  skipped: number;
  progress_percent: number;
  warnings_count: number;
  task_types_created: string[];
  projects_updated: number;
}

// ── Chunked Import Helpers ──────────────────────────────────

async function uploadForChunked(
  file: File,
  options: Record<string, string>
): Promise<TimesheetUploadResponse> {
  const token = storage.getToken();
  const formData = new FormData();
  formData.append('file', file);
  
  Object.entries(options).forEach(([key, value]) => {
    formData.append(key, value);
  });

  const response = await fetch(`${API_BASE_URL}/import/timesheet-upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }
  return data as TimesheetUploadResponse;
}

async function executeChunk(
  jobId: string,
  offset: number,
  limit: number = 30000
): Promise<ChunkExecuteResponse> {
  const token = storage.getToken();
  
  const response = await fetch(
    `${API_BASE_URL}/import/timesheet-execute-chunk?job_id=${jobId}&offset=${offset}&limit=${limit}`,
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();
  if (!response.ok && data.status !== 'error') {
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }
  return data as ChunkExecuteResponse;
}

async function getJobStatus(jobId: string): Promise<JobStatusResponse> {
  const token = storage.getToken();
  
  const response = await fetch(`${API_BASE_URL}/import/timesheet-job-status/${jobId}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }
  return data as JobStatusResponse;
}

async function cleanupJob(jobId: string): Promise<void> {
  const token = storage.getToken();
  
  const response = await fetch(`${API_BASE_URL}/import/timesheet-job-cleanup/${jobId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (response.status === 401) {
    storage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }
}

export const importApi = {
  // Clients & Projects
  downloadTemplate,

  preview: (file: File) =>
    uploadCSV<PreviewResponse>('/import/preview', file),

  execute: (file: File) =>
    uploadCSV<ExecuteResponse>('/import/execute', file),

  // Timesheets
  downloadTimesheetTemplate,

  timesheetPreview: (file: File) =>
    uploadCSV<TimesheetPreviewResponse>('/import/timesheet-preview', file),

  // Quick preview for large files (fast, no validation)
  timesheetQuickPreview: (file: File) =>
    uploadCSV<QuickPreviewResponse>('/import/timesheet-quick-preview', file),

  timesheetExecute: (file: File, options?: TimesheetExecuteOptions) =>
    uploadCSVWithOptions<TimesheetExecuteResponse>('/import/timesheet-execute', file, {
      update_projects: options?.updateProjects ? 'true' : 'false',
    }),

  // Chunked Import (for large files 100k+ rows)
  timesheetUpload: (file: File, options?: TimesheetExecuteOptions) =>
    uploadForChunked(file, {
      update_projects: options?.updateProjects ? 'true' : 'false',
    }),

  timesheetExecuteChunk: executeChunk,

  timesheetJobStatus: getJobStatus,

  timesheetJobCleanup: cleanupJob,
};

export default importApi;
